import { SearchBox, Stack } from '@fluentui/react';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { ITeamReport } from '../../models/reports.internal';
import { objectToArray } from '../../utils/objectUtils';
import { PageSizeContext } from '../../providers/PageSizeProvider';
import { TemporaryDataCacheContext } from '../../providers/TemporaryDataCacheProvider';
import useFormState from '../../shared/hooks/useFormState';
import ExpandToggle from '../../components/shared/ExpandToggle';
import Report from '../../components/shared/Report';
import TeamAgentCards from '../../components/teams/TeamAgentCards';
import TeamOverviewCards from '../../components/teams/TeamOverviewCards';
import MemberSortAndFilterModal, { defaultMemberFilters, MemberFilterOptions, memberSortOptions } from '../../containers/MemberSortAndFilterModal';

const tabs = [
  { key: 'overview', text: 'Overview' },
  { key: 'agents', text: 'Agents' },
];

interface IProps {
  num: number;
  report: ITeamReport;
}

const TeamReport: FC<IProps> = ({ num, report }) => {
  const { storeData, retrieveData } = useContext(TemporaryDataCacheContext);
  const pageSize = useContext(PageSizeContext);
  const [isThresholdEditOpen, setIsThresholdEditOpen] = useState(false) 

  const [tab, setTab] = useState(retrieveData<string>(`TeamReport-${report.id}-tab`) ?? tabs.first.key);
  const updateTab = useCallback(
    (value: string) => {
      setTab(value);
      storeData(`TeamReport-${report.id}-tab`, value, 'session');
    },
    [report.id, storeData],
  );

  const [expanded, setExpanded] = useState(retrieveData<boolean>(`TeamReport-${report.id}-expanded`) ?? false);
  const updateExpanded = useCallback(
    (value: boolean) => {
      setExpanded(value);
      storeData(`TeamReport-${report.id}-expanded`, value, 'session');
    },
    [report.id, storeData],
  );

  const sortedDispositionCodes = useMemo(
    () => objectToArray(report.workItemsByDispositionCode, (key, value) => ({ text: key, value, color: '' })).sortBy(dc => dc.value).reverse(),
    [report.workItemsByDispositionCode],
  );

  const sortedBreakReasons = useMemo(
    () => objectToArray(report.timeSpentInBreakNames, (key, value) => ({ text: key, value, color: '' })).sortBy(bn => bn.value).reverse(),
    [report.timeSpentInBreakNames],
  );

  const [isSortAndFilterOpen, setIsSortAndFilterOpen] = useState(false);

  const [memberSearchText, setMemberSearchText] = useState('');
  const [memberFilters, setMemberFilters] = useFormState<MemberFilterOptions>(defaultMemberFilters);
  const [isSortedDescending, setIsSortedDescending] = useState(false);
  const [memberSortOptionKey, setMemberSortOptionKey] = useState<keyof typeof memberSortOptions>('Name');

  const debouncedSearch = useDebouncedCallback(setMemberSearchText, 800);

  return (
    <Report num={num}
      text={report.name}
      showEdit={false}
      onEditClick={() => setIsThresholdEditOpen(false)}
      tabs={tabs}
      selectedTab={tab}
      onTabChanged={updateTab}
      extraControl={tab === 'overview'
        ? <ExpandToggle isExpanded={expanded} onClick={() => updateExpanded(!expanded)} />
        : (
          <Stack horizontal horizontalAlign="space-between">
            <SearchBox
              placeholder="Search for agents"
              onSearch={setMemberSearchText}
              onClear={() => setMemberSearchText('')}
              onChange={(_, text = '') => debouncedSearch(text)}
              styles={{ root: { marginRight: '0.5rem' } }}
            />
          </Stack>
        )
      }
    >
      {
        tab === 'overview'
          ? <TeamOverviewCards
            expanded={expanded}
            isWideScreen={pageSize.isMediumOrLarger}
            editEnabled={isThresholdEditOpen}
            onEditClose={() => setIsThresholdEditOpen(false)}
            teamReport={report}
            sortedBreakReasons={sortedBreakReasons}
            sortedDispositionCodes={sortedDispositionCodes}
          />
          : <TeamAgentCards
            isWideScreen={pageSize.isLargeOrLarger}
            memberReports={report.memberReports}
            searchText={memberSearchText}
            filters={memberFilters}
            isSortedDescending={isSortedDescending}
            sortOptionKey={memberSortOptionKey}
          />
      }
      <MemberSortAndFilterModal
        isOpen={isSortAndFilterOpen}
        onDismiss={() => setIsSortAndFilterOpen(false)}
        isSortedDescending={isSortedDescending}
        onIsSortedDescendingChange={setIsSortedDescending}
        sortOptionKey={memberSortOptionKey}
        onSortOptionKeyChange={setMemberSortOptionKey}
        filters={memberFilters}
        onFiltersChange={setMemberFilters}
      />
    </Report>
  );
};

export default TeamReport;